import { Check, Loader2, Settings } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { trpc } from "../../lib/trpc";
import { Button, Input, Select, Textarea } from "../ui";

type Props = {
  clubId: string;
  slug: string;
  onCreated: (queueSlug: string) => void;
};

export function CreateQueueForm({ clubId, slug, onCreated }: Props) {
  const utils = trpc.useUtils();
  const { data: games, isLoading: gamesLoading } = trpc.clubGame.list.useQuery({ clubId });

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [clubGameId, setClubGameId] = useState("");

  // Auto-select first game once loaded
  if (games?.length && !clubGameId) {
    setClubGameId(games[0].id);
  }

  const createMutation = trpc.queue.create.useMutation({
    onSuccess: (data) => {
      toast.success("Queue created");
      void utils.queue.list.invalidate();
      if (data?.slug) {
        onCreated(data.slug);
      }
    },
    onError: (err) => toast.error(err.message || "Failed to create queue"),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !clubGameId) return;

    createMutation.mutate({
      clubId,
      clubGameId,
      name: name.trim(),
      description: description.trim() || undefined,
    });
  };

  const isPending = createMutation.isPending;

  if (gamesLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="size-5 animate-spin text-surface-500" />
      </div>
    );
  }

  if (!games?.length) {
    return (
      <div className="rounded-xl bg-surface-800/50 border border-surface-700/50 p-6 text-center space-y-3">
        <p className="text-sm text-surface-400">
          You need to add at least one game to your club before creating a queue. Head to club
          settings to get started.
        </p>
        <Button
          variant="primary"
          onClick={() => {
            window.location.href = `/app/clubs/${slug}/settings`;
          }}
          leftIcon={<Settings className="size-4" />}
        >
          Club Settings
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {games.length > 1 && (
        <div>
          <label className="block text-xs font-medium text-surface-400 mb-1.5">Game</label>
          <Select
            options={games.map((g) => ({ value: g.id, label: g.name }))}
            value={clubGameId}
            onChange={(v) => setClubGameId(v)}
          />
        </div>
      )}

      <Input
        label="Queue Name"
        placeholder="e.g. Ranked 5v5"
        value={name}
        onChange={(e) => setName(e.target.value)}
        maxLength={100}
        autoFocus
      />

      <Textarea
        label="Description"
        placeholder="Optional description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        maxLength={500}
        rows={2}
      />

      <p className="text-xs text-surface-500">
        The queue will be created as disabled. Configure team sizes, match settings, and more before
        enabling.
      </p>

      <div className="flex justify-end">
        <Button
          variant="primary"
          type="submit"
          disabled={!name.trim() || !clubGameId || isPending}
          leftIcon={
            isPending ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />
          }
        >
          {isPending ? "Creating..." : "Create Queue"}
        </Button>
      </div>
    </form>
  );
}
